import { useState } from 'react';
import { Button } from '../ui/button';
import { QuoteSummaryCards } from './QuoteSummaryCards';
import { QuoteTimeSeriesChart } from './QuoteTimeSeriesChart';
import { useQuoteAnalytics } from '../../hooks/useQuoteAnalytics';
import type { QuoteAnalyticsDomain } from '../../services/quote-analytics.service';

interface QuoteAnalyticsTabProps {
  domain: QuoteAnalyticsDomain;
  accessToken: string;
  dateRange: { startDate: string; endDate: string };
}

type Granularity = 'daily' | 'weekly' | 'monthly';
type ChartMode = 'count' | 'value';

export function QuoteAnalyticsTab({ domain, accessToken, dateRange }: QuoteAnalyticsTabProps) {
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const [chartMode, setChartMode] = useState<ChartMode>('count');

  const { summary, dailyData, weeklyData, monthlyData, loading } = useQuoteAnalytics(
    domain,
    accessToken,
    dateRange
  );

  const chartData =
    granularity === 'daily' ? dailyData :
    granularity === 'weekly' ? weeklyData :
    monthlyData;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <QuoteSummaryCards summary={summary} loading={loading} />

      {/* Toggle Row */}
      <div className="flex items-center justify-between">
        {/* Granularity Toggle */}
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={granularity === 'daily' ? 'default' : 'outline'}
            onClick={() => setGranularity('daily')}
          >
            Diário
          </Button>
          <Button
            size="sm"
            variant={granularity === 'weekly' ? 'default' : 'outline'}
            onClick={() => setGranularity('weekly')}
          >
            Semanal
          </Button>
          <Button
            size="sm"
            variant={granularity === 'monthly' ? 'default' : 'outline'}
            onClick={() => setGranularity('monthly')}
          >
            Mensal
          </Button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={chartMode === 'count' ? 'default' : 'outline'}
            onClick={() => setChartMode('count')}
          >
            Quantidade
          </Button>
          <Button
            size="sm"
            variant={chartMode === 'value' ? 'default' : 'outline'}
            onClick={() => setChartMode('value')}
          >
            Valor (R$)
          </Button>
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-[350px] flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Carregando...</div>
        </div>
      ) : (
        <QuoteTimeSeriesChart
          data={chartData}
          mode={chartMode}
          granularity={granularity}
        />
      )}
    </div>
  );
}
